/**
 * React Query hooks for file picker data fetching
 */

import { useQuery } from "@tanstack/react-query"

import {
  listGraphDriveChildrenApi,
  listGraphFilesApi,
  listGraphSharePointLibrariesApi,
  listGraphSharePointSitesApi,
} from "@/api/api"
import { queryKeys } from "@/lib/query-keys"
import { SettingsService } from "@/services/settings-service"

/**
 * Query hook to fetch graph files (OneDrive, sharedWithMe, recent)
 */
export function useGraphFiles(source: string, enabled: boolean = true, pageSize?: number) {
  const settingsService = SettingsService.getInstance()
  const token = settingsService.token

  return useQuery({
    queryKey: queryKeys.graphFiles(source),
    queryFn: () => listGraphFilesApi(source, undefined, pageSize, token),
    enabled: enabled && !!token,
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}

/**
 * Query hook to fetch drive children (nested folders/files)
 */
export function useGraphDriveChildren(
  driveId: string | undefined,
  itemId: string | undefined,
  options?: {
    enabled?: boolean
    pageSize?: number
  }
) {
  const settingsService = SettingsService.getInstance()
  const token = settingsService.token
  const { enabled = true, pageSize } = options || {}

  return useQuery({
    queryKey: queryKeys.graphDriveChildren(driveId || "", itemId || ""),
    queryFn: () => listGraphDriveChildrenApi(driveId!, itemId!, undefined, pageSize, token),
    enabled: enabled && !!driveId && !!itemId && !!token,
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}

/**
 * Query hook to fetch SharePoint sites
 */
export function useSharePointSites(
  enabled: boolean = true,
  mode: "auto" | "followed" | "discovered" = "auto",
  pageSize?: number
) {
  const settingsService = SettingsService.getInstance()
  const token = settingsService.token

  return useQuery({
    queryKey: queryKeys.sharePointSites(),
    queryFn: () => listGraphSharePointSitesApi(mode, undefined, pageSize, token),
    enabled: enabled && !!token,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes (sites change less frequently)
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
  })
}

/**
 * Query hook to fetch SharePoint libraries for a site
 */
export function useSharePointLibraries(
  siteId: string | undefined,
  enabled: boolean = true,
  pageSize?: number
) {
  const settingsService = SettingsService.getInstance()
  const token = settingsService.token

  return useQuery({
    queryKey: queryKeys.sharePointLibraries(siteId || ""),
    queryFn: () => listGraphSharePointLibrariesApi(siteId!, undefined, pageSize, token),
    enabled: enabled && !!siteId && !!token,
    staleTime: 1000 * 60 * 3, // Consider data fresh for 3 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}
